
import * as THREE from 'three';
import { createWireTitle } from '../../../utils/createWireTitle';
import { createSolidHatchFromBoundary } from '../../../commands/createSolidHatchFromBoundary';
import { generateUniqueId } from '../../../utils/generateUniqueId';

/**
 * Polyline üzerinde belirli bir mesafedeki noktayı hesaplar
 * @param {THREE.Vector3[]} verts - Polyline vertex'leri
 * @param {number} d - Başlangıçtan itibaren mesafe
 * @returns {THREE.Vector3|null} - Hesaplanan nokta
 */
function getPointAlongPolyline(verts, d) {
  if (!Array.isArray(verts) || verts.length === 0) return null;
  if (d <= 0) return verts[0].clone();
  let remaining = d;
  for (let i = 0; i < verts.length - 1; i++) {
    const a = verts[i];
    const b = verts[i + 1];
    const seg = new THREE.Vector3().subVectors(b, a);
    const segLen = seg.length();
    if (remaining <= segLen) return a.clone().add(seg.normalize().multiplyScalar(remaining));
    remaining -= segLen;
  }
  return verts[verts.length - 1].clone();
}

/**
 * Panel sürüklendiğinde, title linelarının wire tarafındaki uçlarını hareket ettirir.
 * Title line, wireTitle grubunun "titleLine" isimli child'ıdır.
 * Line'ın index 1 noktası (0,0,0) wire'a bağlı uçtur.
 * @param {THREE.Scene} scene - Three.js sahnesi
 * @param {string} panelID - Panel ID
 * @param {{x: number, y: number, z: number}} direction - Hareket vektörü
 */
export function moveWireTitleLineWireEnds(scene, panelID, direction) {
  if (!scene || !panelID || !direction) {
    return;
  }

  let updatedCount = 0;

  // wireTitle gruplarını bul
  scene.traverse(obj => {
    if (obj.userData?.type === 'wireTitle' &&
        (obj.userData?.panelId === panelID || obj.userData?.panelID === panelID)) {

      // titleLine child'ını bul
      obj.traverse(child => {
        if (child.name === 'titleLine' && child.geometry?.attributes?.position) {
          const positions = child.geometry.attributes.position;

          // Line'ın wire tarafındaki ucu (index 1, local koordinat (0,0,0))
          // Bu uç group'un position'ına göre local koordinatlarda
          // Panel hareket ettiğinde wireVertices güncelleniyor,
          // ancak line local koordinatlarda olduğu için
          // aslında group position değişince line de hareket ediyor.

          // Eğer line'ın geometry'si güncellenecekse:
          // Wire tarafındaki uç index 1'de (0,0,0)
          // Circle tarafındaki uç index 0'da (0, lineLength, 0)

          updatedCount++;
        }
      });
    }
  });
}

/**
 * Remove any existing wire title groups for a given panel from the scene.
 * @param {THREE.Scene} scene
 * @param {string} panelID
 */
function clearPanelWireTitles(scene, panelID) {
  const toRemove = [];
  scene.traverse((child) => {
    if ((child.userData?.type === 'wireTitle' || child.name === 'wireTitle') &&
        (child.userData?.panelId === panelID || child.userData?.panelID === panelID)) {
      toRemove.push(child);
    }
  });

  toRemove.forEach((title) => {
    // detach from parent (scene or group)
    if (title.parent) title.parent.remove(title);
    else scene.remove(title);

    // dispose geometry and material resources
    title.traverse((obj) => {
      try {
        if (obj.geometry) obj.geometry.dispose();
      } catch (e) {}
      try {
        if (obj.material) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach(m => {
            if (!m) return;
            // ⚠️ material.dispose() texture'ı (map) BIRAKMAZ → ayrıca dispose et.
            // Başlık yazıları CanvasTexture; updatePanelTitles her çizimde çağrıldığı
            // için bu leak oturum boyunca yüzlerce texture biriktiriyordu.
            if (m.map && m.map.dispose) m.map.dispose();
            if (m.alphaMap && m.alphaMap.dispose) m.alphaMap.dispose();
            if (m.dispose) m.dispose();
          });
        }
      } catch (e) {}
    });
  });
}

export function updatePanelTitles(scene, panelID, forceUpdate = false) {
  if (!panelID) return;

  // ✅ Only update titles if forced (e.g. by "Etiket Güncelle" button)
  if (!forceUpdate) return;

  // Panel objesini bul
  let panel = null;
  let candidatePanels = [];
  scene.traverse((child) => {
    if (child.userData?.id === panelID) {
      candidatePanels.push({ obj: child, type: child.userData?.type, name: child.name, id: child.userData?.id });
    }
    if ((child.userData?.type === 'mainPanel' || child.userData?.type === 'panel') && child.userData?.id === panelID) {
      panel = child;
    }
  });
  if (!panel && candidatePanels.length > 0) panel = candidatePanels[0].obj;
  if (!panel) return;

  // ✅ Önce mevcut etiketleri kontrol et
  let existingTitles = [];
  scene.traverse((child) => {
    if ((child.userData?.type === 'wireTitle' || child.name === 'wireTitle') &&
        (child.userData?.panelId === panelID || child.userData?.panelID === panelID)) {
      existingTitles.push(child);
    }
  });

  // ✅ Eğer etiketler zaten varsa, sadece numaraları güncelle (pozisyonlar değişmez)
  if (existingTitles.length > 0) {
     updateWireTitleNumbers(scene, panelID);
    return;
  }

  // Etiket yoksa yeni oluştur
 
  const outs = panel.userData.outs || [];
  if (outs.length === 0) return;

  const wiringLines = findPanelWiringLines(scene, panelID);
  const exitPoints = findPanelExitPoints(panel);

  // placement params
  const minStartOffset = 10;
  const baseSpacing = 16;
  const minSeparation = 18;
  const maxNudgeDistance = 20;

  // precompute scene spheres
  const sceneCollisionSpheres = [];
  scene.traverse((child) => {
    if (!child.geometry || child.userData?.type === 'wireTitle' || child.userData?.isPanelPart) return;
    try {
      const box = new THREE.Box3().setFromObject(child);
      const sphere = box.getBoundingSphere(new THREE.Sphere());
      if (sphere && sphere.radius > 0.001) sceneCollisionSpheres.push(sphere);
    } catch (e) {}
  });

  const getPointAlong = (verts, d) => {
    if (!Array.isArray(verts) || verts.length === 0) return null;
    if (d <= 0) return verts[0].clone();
    let remaining = d;
    for (let i = 0; i < verts.length - 1; i++) {
      const a = verts[i];
      const b = verts[i + 1];
      const seg = new THREE.Vector3().subVectors(b, a);
      const segLen = seg.length();
      if (remaining <= segLen) return a.clone().add(seg.normalize().multiplyScalar(remaining));
      remaining -= segLen;
    }
    return verts[verts.length - 1].clone();
  };

  const getPolylineLength = (verts) => {
    if (!Array.isArray(verts) || verts.length < 2) return 0;
    let len = 0;
    for (let i = 0; i < verts.length - 1; i++) len += verts[i].distanceTo(verts[i + 1]);
    return len;
  };

  const placedPositions = [];
  const usedOutIndices = new Set();

  // Iterate physical exit points (ordered) and match outs to them
  for (let exitIndex = 0; exitIndex < exitPoints.length; exitIndex++) {
    const exitPoint = exitPoints[exitIndex];
    const worldPosition = new THREE.Vector3();
    exitPoint.getWorldPosition(worldPosition);

    let matchedOut = null;
    let matchingWire = null;

    const tryMatchOutByWire = (out) => {
      if (!out) return null;
      if (out.wires && Array.isArray(out.wires) && out.wires.length > 0) {
        const wireData = out.wires[0];
        const wireId = wireData && (wireData.id || wireData);
        if (wireId) {
          const w = wiringLines.find(wl => wl.userData && (wl.userData.id === wireId || wl.userData.wireID === wireId));
          if (w) return w;
        }
      }
      return null;
    };

    // 1) explicit wire references near the exit (skip outs already used)
    for (let oi = 0; oi < outs.length; oi++) {
      if (usedOutIndices.has(oi)) continue;
      const outCandidate = outs[oi];
      const w = tryMatchOutByWire(outCandidate);
      if (w) {
        try {
          const posAttr = w.geometry?.attributes?.position;
          if (posAttr && posAttr.count > 0) {
            const first = new THREE.Vector3(posAttr.getX(0), posAttr.getY(0), posAttr.getZ(0));
            if (first.distanceTo(worldPosition) <= 40) {
              matchedOut = outCandidate;
              matchingWire = w;
              usedOutIndices.add(oi);
              break;
            }
          }
        } catch (e) {}
      }
    }

    // 2) id/branch matching
    if (!matchedOut) {
      for (let oi = 0; oi < outs.length; oi++) {
        if (usedOutIndices.has(oi)) continue;
        const outCandidate = outs[oi];
        const found = wiringLines.find(w =>
          w.userData.id === outCandidate.lineId ||
          w.userData.branchID === outCandidate.branchID ||
          w.userData.wireID === outCandidate.lineId
        );
        if (found) {
          try {
            const posAttr = found.geometry?.attributes?.position;
            if (posAttr && posAttr.count > 0) {
              const first = new THREE.Vector3(posAttr.getX(0), posAttr.getY(0), posAttr.getZ(0));
              if (first.distanceTo(worldPosition) <= 60) {
                matchedOut = outCandidate;
                matchingWire = found;
                usedOutIndices.add(oi);
                break;
              }
            }
          } catch (e) {
            matchedOut = outCandidate;
            matchingWire = found;
            usedOutIndices.add(oi);
            break;
          }
        }
      }
    }

    // 3) machine/component heuristics
    if (!matchedOut) {
      for (let oi = 0; oi < outs.length; oi++) {
        if (usedOutIndices.has(oi)) continue;
        const outCandidate = outs[oi];
        const found = wiringLines.find(wire => {
          const ud = wire.userData || {};
          if (ud.componentId && outCandidate.componentId && ud.componentId === outCandidate.componentId) return true;
          if (ud.componentId && outCandidate.id && ud.componentId === outCandidate.id) return true;
          if (ud.parent === 'machine' || ud.layer === 'machine' || ud.type === 'machine') {
            if (ud.panelID && outCandidate.panelID && ud.panelID === outCandidate.panelID) return true;
            if (ud.branchID && outCandidate.branchID && ud.branchID === outCandidate.branchID) return true;
          }
          return false;
        });
        if (found) {
          matchedOut = outCandidate;
          matchingWire = found;
          usedOutIndices.add(oi);
          break;
        }
      }
    }

    // 4) spatial fallback: find nearest unused out by first-vertex proximity
    if (!matchedOut && exitPoint) {
      const proximityThreshold = 60;
      let bestOi = -1;
      let bestDist = Infinity;
      for (let oi = 0; oi < outs.length; oi++) {
        if (usedOutIndices.has(oi)) continue;
        const outCandidate = outs[oi];
        // prefer explicit vertices
        if (outCandidate.vertices && outCandidate.vertices.length > 0) {
          const v = outCandidate.vertices[0];
          const first = v instanceof THREE.Vector3 ? v : new THREE.Vector3(v.x, v.y, v.z || 0);
          const d = first.distanceTo(worldPosition);
          if (d < bestDist) { bestDist = d; bestOi = oi; }
        } else if (outCandidate.wires && outCandidate.wires.length > 0) {
          // try to locate wire and use its first vertex
          const wireData = outCandidate.wires[0];
          const wireId = wireData && (wireData.id || wireData);
          if (wireId) {
            const w = wiringLines.find(wl => wl.userData && (wl.userData.id === wireId || wl.userData.wireID === wireId));
            if (w && w.geometry && w.geometry.attributes && w.geometry.attributes.position) {
              try {
                const posAttr = w.geometry.attributes.position;
                if (posAttr.count > 0) {
                  const first = new THREE.Vector3(posAttr.getX(0), posAttr.getY(0), posAttr.getZ(0));
                  const d = first.distanceTo(worldPosition);
                  if (d < bestDist) { bestDist = d; bestOi = oi; }
                }
              } catch (e) {}
            }
          }
        }
      }
      if (bestOi !== -1 && bestDist <= proximityThreshold) {
        matchedOut = outs[bestOi];
        usedOutIndices.add(bestOi);
        // try to set matchingWire from wires if available
        if (matchedOut.wires && matchedOut.wires.length > 0) {
          const wireData = matchedOut.wires[0];
          const wireId = wireData && (wireData.id || wireData);
          if (wireId) matchingWire = wiringLines.find(wl => wl.userData && (wl.userData.id === wireId || wl.userData.wireID === wireId));
        }
      }

    }

    // final fallback: use the outs entry at the same index only if unused
    if (!matchedOut) {
      if (outs[exitIndex] && !usedOutIndices.has(exitIndex)) {
        matchedOut = outs[exitIndex];
        usedOutIndices.add(exitIndex);
      }
    }

    const titleText = `${exitIndex + 1}`;
    const titleColor = '#00ffff';

    let vertices = [];
    if (matchedOut && matchedOut.vertices && Array.isArray(matchedOut.vertices) && matchedOut.vertices.length > 1) {
      vertices = matchedOut.vertices.map(v => (v instanceof THREE.Vector3 ? v.clone() : new THREE.Vector3(v.x, v.y, v.z || 0)));
    } else if (matchingWire && matchingWire.geometry) {
      const geometry = matchingWire.geometry;
      const positions = geometry.attributes.position;
      vertices = [];
      for (let i = 0; i < positions.count; i++) {
        vertices.push(new THREE.Vector3(positions.getX(i), positions.getY(i), positions.getZ(i)));
      }
    } else if (exitPoint) {
      vertices = [worldPosition.clone(), worldPosition.clone().add(new THREE.Vector3(50, 0, 0))];
    } else {
      continue;
    }

    // Re-orient vertices so the start is nearest the panel exit
    if (exitPoint && Array.isArray(vertices) && vertices.length > 1) {
      try {
        const worldPos = new THREE.Vector3();
        exitPoint.getWorldPosition(worldPos);
        let nearestIdx = 0;
        let nearestDist = Infinity;
        for (let vi = 0; vi < vertices.length; vi++) {
          const d = vertices[vi].distanceTo(worldPos);
          if (d < nearestDist) { nearestDist = d; nearestIdx = vi; }
        }
        let goForward = true;
        const nextIdx = nearestIdx + 1;
        const prevIdx = nearestIdx - 1;
        let distNext = -1; let distPrev = -1;
        if (nextIdx < vertices.length) distNext = vertices[nextIdx].distanceTo(worldPos);
        if (prevIdx >= 0) distPrev = vertices[prevIdx].distanceTo(worldPos);
        if (distPrev > distNext && distPrev >= 0) goForward = false;
        const ordered = [];
        if (goForward) {
          for (let k = nearestIdx; k < vertices.length; k++) ordered.push(vertices[k].clone());
          for (let k = 0; k < nearestIdx; k++) ordered.push(vertices[k].clone());
        } else {
          for (let k = nearestIdx; k >= 0; k--) ordered.push(vertices[k].clone());
          for (let k = vertices.length - 1; k > nearestIdx; k--) ordered.push(vertices[k].clone());
        }
        vertices = ordered;
      } catch (e) {}
    }

    const totalLen = getPolylineLength(vertices);
    let startOffset = minStartOffset;
    let candidateDistance = startOffset + (exitIndex * baseSpacing);
    if (candidateDistance > totalLen) candidateDistance = Math.min(totalLen, startOffset + (exitIndex * baseSpacing));

    const minClearanceToObjects = 8;
    const nudgeStep = Math.max(minSeparation, 8);
    let attempts = 0;
    let placedPos = getPointAlong(vertices, candidateDistance) || vertices[0].clone();
    const maxAttempts = Math.ceil(maxNudgeDistance / nudgeStep);

    const isTooCloseToPlaced = (p) => placedPositions.some(prev => p.distanceTo(prev) < minSeparation);
    const isTooCloseToScene = (p) => {
      for (let s of sceneCollisionSpheres) {
        const dist = p.distanceTo(s.center);
        if (dist < s.radius + minClearanceToObjects) return true;
      }
      return false;
    };

    while ((isTooCloseToPlaced(placedPos) || isTooCloseToScene(placedPos)) && attempts < maxAttempts) {
      attempts++;
      candidateDistance += nudgeStep;
      if (candidateDistance > totalLen) {
        candidateDistance = totalLen;
        placedPos = getPointAlong(vertices, candidateDistance) || vertices[vertices.length - 1].clone();
        break;
      }
      placedPos = getPointAlong(vertices, candidateDistance) || placedPos;
    }

    if (isTooCloseToPlaced(placedPos)) {
      const dir = new THREE.Vector3();
      if (vertices.length >= 2) dir.subVectors(vertices[1], vertices[0]).normalize();
      else dir.set(1, 0, 0);
      placedPos.add(dir.multiplyScalar(minSeparation));
    }

    placedPositions.push(placedPos.clone());

    const baseLineLen = 12;
    const initialLineLength = baseLineLen + ((exitIndex % 3) * 8);

    const titleGroup = createWireTitle(
      placedPos,
      titleText,
      titleColor,
      vertices,
      (matchedOut && (matchedOut.lineId || (matchedOut.wires && matchedOut.wires[0] && matchedOut.wires[0].id))) || `wire-${exitIndex}`,
      candidateDistance,
      initialLineLength
    );

    titleGroup.userData.panelId = panelID;
    titleGroup.userData.panelID = panelID;
    titleGroup.userData.exitIndex = exitIndex;
    titleGroup.userData.type = 'wireTitle';

    scene.add(titleGroup);
  }
}


/**
 * Panel hatch'ini yeniden oluşturur (orijinal tasarıma uygun)
 * @param {THREE.Group} offsetGroup - Panel offset group
 * @param {number} halfW - Panel yarı genişliği
 * @param {number} halfH - Panel yarı yüksekliği  
 * @param {number} symmetricalOffset - Simetrik offset
 * @param {number} dir - Yön
 */
function recreatePanelHatch(offsetGroup, halfW, halfH, symmetricalOffset, dir) {
  const hatchElement = {
    handle: generateUniqueId(),
    SolidFillFlag: 1,
    HatchPatternName: "SOLID",
  };

  // Groups düz dizi olmalı (addPathToShape her segmenti ayrı edge olarak işler);
  // createPanel.js ile birebir aynı yapı.
  const hatchPaths = [
    {
      type: "outer",
      groups: [
        {
          edgeType: "Line",
          x1: -halfW + symmetricalOffset * dir,
          y1: halfH,
          x2: halfW + symmetricalOffset * dir,
          y2: -halfH
        },
        {
          edgeType: "Line",
          x1: halfW + symmetricalOffset * dir,
          y1: -halfH,
          x2: -halfW + symmetricalOffset * dir,
          y2: -halfH
        }
      ]
    }
  ];

  const hatch = createSolidHatchFromBoundary(hatchElement, {
    boundaryPaths: hatchPaths,
    color: 0xff0000,
    scene: null, // Sahneye eklenmeyecek, group'a eklenecek
    group: offsetGroup,
  });

  if (hatch) {
    hatch.name = 'panel-hatch';
    hatch.userData = { ...hatch.userData, isPanelPart: true };
    offsetGroup.add(hatch);
  }
}

/**
 * Çıkış sayısına göre gerekli panel yüksekliğini hesaplar
 * @param {number} exitCount - Çıkış sayısı
 * @returns {number} - Gerekli panel yüksekliği
 */
export function calculateRequiredPanelHeight(exitCount) {
  const minHeight = 25; // Minimum panel yüksekliği
  const minExitSpacing = 5; // Çıkışlar arası minimum mesafe
  const maxExitsForMinHeight = 4; // Minimum yüksekliğe sığan maksimum çıkış sayısı
  
  if (exitCount <= maxExitsForMinHeight) {
    return minHeight;
  }
  
  // 4'ten fazla çıkış varsa, panel yüksekliğini artır
  // Her ek çıkış için 5 birim ek yükseklik
  const extraExits = exitCount - maxExitsForMinHeight;
  return minHeight + (extraExits * minExitSpacing);
}

/**
 * Panel boyutunu çıkış sayısına göre günceller
 * @param {THREE.Object3D} panel - Panel object
 * @param {number} exitCount - Çıkış sayısı
 * @param {THREE.Scene} scene - Scene object (kullanılmıyor, geriye uyumluluk için)
 */
export function updatePanelSize(panel, exitCount, scene = null) {
  // MainPanel sabit boyutlu — kendi polyline/hatch isimleri farklı ('mainPanel-outer',
  // 'mainPanel-hatch1/2') ve height 50 olarak yaratılır. Resize akışı burada
  // frontEdge'i yanlış boyuta çekip userData.height'ı bozuyordu → görsel
  // bozulma. MainPanel'de hiçbir geometrik güncelleme yapmayacağız.
  if (panel?.userData?.type === 'mainPanel') return;

  // ✅ Mevcut height'ı değişiklikten ÖNCE kaydet (wire henüz tamamlanmamış olabilir)
  panel.userData.oldHeight = panel.userData.height || calculateRequiredPanelHeight(exitCount);

  const newHeight = calculateRequiredPanelHeight(exitCount);
  const halfW = 7.5; // Panel genişliği/2 (sabit)
  const halfH = newHeight / 2;
  
  const symmetricalOffset = panel.userData.symmetricalOffset || 0;
  const dir = panel.userData.dir || 1;
  
  // Panel'in offsetGroup'unu bul
  let offsetGroup = null;
  panel.traverse((child) => {
    if (child.name === 'offsetGroup') {
      offsetGroup = child;
    }
  });
  
  if (!offsetGroup) {
    return;
  }
  
  // Panel ana çerçevesini (polyline) güncelle
  offsetGroup.traverse((child) => {
    if (child.name === 'panel-outer' && child.type === 'Line') {
      const newPoints = [
        new THREE.Vector3(-halfW + symmetricalOffset * dir, -halfH, 0),
        new THREE.Vector3(-halfW + symmetricalOffset * dir, halfH, 0),
        new THREE.Vector3(halfW + symmetricalOffset * dir, halfH, 0),
        new THREE.Vector3(halfW + symmetricalOffset * dir, -halfH, 0),
        new THREE.Vector3(-halfW + symmetricalOffset * dir, -halfH, 0) // Kapalı çizgi için
      ];
      child.geometry.setFromPoints(newPoints);
    }
    
    // Kırmızı ön kenar çizgisini güncelle
    if (child.userData?.lineType === 'frontEdge') {
      const newFrontPoints = [
        new THREE.Vector3(halfW + symmetricalOffset * dir, -halfH, 0),
        new THREE.Vector3(halfW + symmetricalOffset * dir, halfH, 0)
      ];
      child.geometry.setFromPoints(newFrontPoints);
    }
    
    // Panel dolgu alanını (hatch) yeniden oluştur
    if (child.name === 'panel-hatch') {
      // Mevcut hatch'i sil
      offsetGroup.remove(child);
      child.geometry?.dispose();
      child.material?.dispose();
      
      // Yeni hatch oluştur (orijinal yöntemi kullan)
      recreatePanelHatch(offsetGroup, halfW, halfH, symmetricalOffset, dir);
    }
  });
  
  // Panel userData'sını güncelle
  panel.userData.height = newHeight;
  panel.userData.startH = newHeight / 2;
  panel.userData.endH = newHeight / 2 - 5;
  
  // ✅ Wire güncelleme yapılmayacak - wire tamamlandığında updatePanelInputWire çağrılacak
}

/**
 * Panel giriş hattının son iki köşesini panelin giriş kenarının ortasına günceller
 * Wire tamamlandığında (Enter) çağrılmalı
 * @param {THREE.Scene} scene - Scene object
 * @param {THREE.Object3D} panel - Panel object
 */
export function updatePanelInputWire(scene, panel) {
  const panelID = panel.userData.id;

  // Find offsetGroup to get the visual center
  let offsetGroup = null;
  panel.traverse(child => {
    if (child.name === 'offsetGroup') offsetGroup = child;
  });

  if (!offsetGroup) return;

  // Calculate the target connection point (midpoint of the input edge)
  const halfW = 7.5;
  const symmetricalOffset = panel.userData.symmetricalOffset || 0;
  const dir = panel.userData.dir || 1;

  // Local coordinates of the input edge center (Left edge)
  const localTarget = new THREE.Vector3(-halfW + symmetricalOffset * dir, 0, 0);

  // Transform to world coordinates
  offsetGroup.updateMatrixWorld();
  const targetWorld = localTarget.applyMatrix4(offsetGroup.matrixWorld);
  
  // Collect output wire IDs to exclude them
  const outputWireIds = new Set();
  if (panel.userData.outs && Array.isArray(panel.userData.outs)) {
    panel.userData.outs.forEach(out => {
      if (out.lineId) outputWireIds.add(out.lineId);
      if (out.wires && Array.isArray(out.wires)) {
        out.wires.forEach(w => {
            if (w.id) outputWireIds.add(w.id);
            else outputWireIds.add(w);
        });
      }
    });
  }

  // Panel taşımadaki mantıkla wire'ları bul
  const connectedWireIds = panel.userData.connectedWires || [];
  let wiringLines = [];
  const addedWireIds = new Set();

  // 1. connectedWires'tan wire'ları al
  if (connectedWireIds.length > 0) {
    connectedWireIds.forEach(wireId => {
      scene.traverse(obj => {
        if (obj.type === 'Line' && obj.userData?.id === wireId) {
          wiringLines.push(obj);
          addedWireIds.add(wireId);
        }
      });
    });
  }

  // 2. findPanelWiringLines'tan da wire'ları al (çakışmaları önle)
  const panelWiringLines = findPanelWiringLines(scene, panelID);
  panelWiringLines.forEach(wireLine => {
    const wireId = wireLine.userData?.id;
    if (wireId && !addedWireIds.has(wireId)) {
      wiringLines.push(wireLine);
      addedWireIds.add(wireId);
    }
  });
  
  if (wiringLines.length === 0) {
    return;
  }

  // Her wire için kontrol et - hangisi giriş hattı?
  wiringLines.forEach(wireLine => {
    // Skip if it's an output wire
    if (outputWireIds.has(wireLine.userData.id)) return;

    if (!wireLine.geometry || !wireLine.geometry.attributes.position) {
      return;
    }

    const positions = wireLine.geometry.attributes.position.array;
    const vertexCount = positions.length / 3;

    if (vertexCount < 2) {
      return;
    }

    // Wire'ın hangi ucunun panele bağlı olduğunu belirle
    // Son vertex panele yakınsa → giriş hattı (besleme hattı)
    // İlk vertex panele yakınsa → çıkış hattı (skip)
    const firstX = positions[0];
    const firstY = positions[1];
    const firstZ = positions[2];
    const firstPoint = new THREE.Vector3(firstX, firstY, firstZ);

    const lastIndex = (vertexCount - 1) * 3;
    const lastX = positions[lastIndex];
    const lastY = positions[lastIndex + 1];
    const lastZ = positions[lastIndex + 2];
    const lastPoint = new THREE.Vector3(lastX, lastY, lastZ);

    // Mesh-FP migration sonrası: panel.position ve wire vertex'leri farklı
    // koord space'lerinde olabilir (panel fpMesh-local, wire vertices wire-local).
    // Karşılaştırmayı her zaman WORLD space'te yap → uyumsuzluk olmaz.
    wireLine.updateMatrixWorld();
    const wireMatrixWorld = wireLine.matrixWorld;
    const panelWorldPos = new THREE.Vector3();
    panel.getWorldPosition(panelWorldPos);
    const firstWorld = firstPoint.clone().applyMatrix4(wireMatrixWorld);
    const lastWorld = lastPoint.clone().applyMatrix4(wireMatrixWorld);

    // Her iki ucun da panele olan WORLD mesafesini hesapla
    const distFirstToPanel = firstWorld.distanceTo(panelWorldPos);
    const distLastToPanel = lastWorld.distanceTo(panelWorldPos);

    // Eğer ilk vertex son vertex'ten panele daha yakınsa, bu bir ÇIKIŞ hattidir (skip)
    if (distFirstToPanel < distLastToPanel) {
      return; // Çıkış hattı, güncelleme yapma
    }

    // Eğer son nokta panele yakınsa, bu bir giriş hattıdır (güncelle)
    if (distLastToPanel < 100) {
        // targetWorld WORLD space'te. Wire vertex'leri wire-LOCAL'da
        // saklanır, ona yazmak için targetWorld'ü wire LOCAL'ına çevir.
        const wireInverseMatrix = new THREE.Matrix4().copy(wireMatrixWorld).invert();
        const targetLocal = targetWorld.clone().applyMatrix4(wireInverseMatrix);

        const deltaX = targetLocal.x - lastX;
        const deltaY = targetLocal.y - lastY;
        const deltaZ = targetLocal.z - lastZ;

        // Son noktayı targetLocal'a taşı (wire LOCAL space)
        positions[lastIndex] = targetLocal.x;
        positions[lastIndex + 1] = targetLocal.y;
        positions[lastIndex + 2] = targetLocal.z;

        // Sondan bir önceki noktayı da aynı miktarda ötele (wire LOCAL delta)
        if (vertexCount >= 2) {
            const prevIndex = (vertexCount - 2) * 3;
            positions[prevIndex] += deltaX;
            positions[prevIndex + 1] += deltaY;
            positions[prevIndex + 2] += deltaZ;
        }

        wireLine.geometry.attributes.position.needsUpdate = true;
        wireLine.geometry.computeBoundingSphere();

        // userData.vertices wire-LOCAL koord ile aynı space → delta ile öteler
        if (wireLine.userData.vertices && wireLine.userData.vertices.length >= 1) {
            const numVertices = wireLine.userData.vertices.length;
            // Son vertex'i wire LOCAL'a set et
            wireLine.userData.vertices[numVertices - 1].x = targetLocal.x;
            wireLine.userData.vertices[numVertices - 1].y = targetLocal.y;
            wireLine.userData.vertices[numVertices - 1].z = targetLocal.z;

            // Sondan bir önceki delta ile öteler
            if (numVertices >= 2) {
                wireLine.userData.vertices[numVertices - 2].x += deltaX;
                wireLine.userData.vertices[numVertices - 2].y += deltaY;
                wireLine.userData.vertices[numVertices - 2].z += deltaZ;
            }
        }
    }
  });
  
  // ✅ oldHeight'ı yeni değere güncelle (bir sonraki değişiklik için)
  panel.userData.oldHeight = panel.userData.height;
}

/**
 * Panel'deki kırmızı çizgiyi bulur
 * @param {THREE.Object3D} panel - Panel object
 * @returns {THREE.Line|null} - Kırmızı çizgi object
 */
export function findPanelFrontEdgeLine(panel) {
  let frontEdgeLine = null;

  panel.traverse((child) => {
    if (child.userData?.lineType === 'frontEdge' && child.userData?.panelID) {
      frontEdgeLine = child;
    }
  });

  return frontEdgeLine;
}

/**
 * Panel'deki mevcut çıkış noktalarını bulur
 * @param {THREE.Object3D} panel - Panel object  
 * @returns {THREE.Mesh[]} - Çıkış noktası daireleri
 */
export function findPanelExitPoints(panel) {
  const exitPoints = [];

  panel.traverse((child) => {
    if (child.userData?.isExitPoint && child.userData?.panelID) {
      exitPoints.push(child);
    }
  });

  // Index'e göre sırala
  return exitPoints.sort((a, b) => a.userData.exitIndex - b.userData.exitIndex);
}

/**
 * Panel için yeni çıkış noktaları oluşturur
 * @param {THREE.Object3D} panel - Panel object
 * @param {number} exitCount - İstenen çıkış sayısı
 * @param {THREE.Scene} scene - Scene object (optional, panel giriş hattını güncellemek için)
 */
export function updatePanelExitPoints(panel, exitCount, scene = null, addPosition = null, skipWiringPositions = false) {
  const frontEdgeLine = findPanelFrontEdgeLine(panel);
  if (!frontEdgeLine) {
    return;
  }

  // Önce panel boyutunu çıkış sayısına göre ayarla
  updatePanelSize(panel, exitCount, scene);

  // ✅ Panel giriş hattını güncelle (boyut değiştiyse)
  if (scene) {
    updatePanelInputWire(scene, panel);
  }

  // Mevcut çıkış noktalarını temizle
  const existingExitPoints = findPanelExitPoints(panel);
  existingExitPoints.forEach(point => {
    point.parent.remove(point);
    point.geometry.dispose();
    point.material.dispose();
  });

  // Panel parametrelerini al
  const panelID = panel.userData.id;
  const halfW = 7.5; // Panel genişliği/2
  const isMainPanel = panel.userData?.type === 'mainPanel';
  // MainPanel resize olmuyor — kendi height'ını kullan; diğerleri exitCount'a bağlı
  const newHeight = isMainPanel
    ? (panel.userData?.height || 50)
    : calculateRequiredPanelHeight(exitCount);
  const halfH = newHeight / 2; // Güncellenmiş yükseklik
  const symmetricalOffset = panel.userData.symmetricalOffset || 0;
  const dir = panel.userData.dir || 1;

  // Kırmızı çizginin koordinatları
  const lineStartY = -halfH;
  const lineEndY = halfH;
  const lineX = halfW + symmetricalOffset * dir;

  // Çıkış noktalarını hesapla (sabit 5 birim aralık)
  const segmentLength = 5;

  // Panel'in offsetGroup'unu bul
  let offsetGroup = null;
  panel.traverse((child) => {
    if (child.name === 'offsetGroup') {
      offsetGroup = child;
    }
  });

  if (!offsetGroup) {
    return;
  }
  
  // Her segment noktasında daire oluştur
  // Çıkış noktalarını ortala
  const totalSpan = (exitCount - 1) * segmentLength;
  const startY = -totalSpan / 2;

  for (let i = 0; i < exitCount; i++) {
    const pointY = startY + (i * segmentLength);
    const pointPosition = new THREE.Vector3(lineX, pointY, 0);
    const exitIndex = i + 1;

    // Küçük daire oluştur
    const circleRadius = 1;
    const circleGeometry = new THREE.CircleGeometry(circleRadius, 8);
    const circleMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ff00, // Yeşil
      side: THREE.DoubleSide
    });
    const circle = new THREE.Mesh(circleGeometry, circleMaterial);

    circle.position.copy(pointPosition);
    circle.name = `panel-exit-point-${exitIndex}`;
    circle.userData = {
      type: 'circle', // DXF export için tip belirt
      isPanelPart: true,
      panelID,
      isExitPoint: true,
      exitIndex: exitIndex,
      id: `exit-point-${panelID}-${exitIndex}`,
      // DXF export için gerekli circle bilgileri
      center: { x: pointPosition.x, y: pointPosition.y },
      radius: circleRadius,
      layerName: 'Defpoints' // Panel exit point'leri export edilmemeli (dahili işaretçi)
    };

    offsetGroup.add(circle);
  }

  // NOTE: Do not auto-create wire titles here. Titles should be created when
  // the wire/component is finalized (Enter). Calling updatePanelTitles during
  // exit point creation caused titles to appear prematurely. Use
  // `notifyPanelOutsChanged(panel)` if callers must trigger titles outside
  // of the normal finalize flow.

  // ✅ Yeşil noktalar güncellendiğinde wire'ları da otomatik ötele
  // Bu sayede çağıran yerlerde ayrıca updatePanelWiringPositions çağırmaya gerek kalmaz
  // skipWiringPositions=true → RESTORE senaryosu: wire vertex'leri save'den
  // doğru pos'ta geliyor, default exit pos'a ezme — kullanıcı düzenlemesi
  // korunsun (yol A: mimari modüler izolasyon).
  if (scene && exitCount > 0 && !skipWiringPositions) {
    updatePanelWiringPositions(scene, panel, addPosition);
  }
}

/**
 * Panel'deki mevcut çıkışı günceller (duplicate ekleme yerine)
 * @param {THREE.Object3D} panel - Panel object
 * @param {Object} exitData - Çıkış verisi {type, lineId, socketId, etc.}
 * @param {string} addPosition - 'start' veya 'end' (varsayılan: 'start')
 */
export function addPanelExit(panel, exitData, addPosition = 'start') {
  if (!panel || !panel.userData) return;
  
  // outs array'ini başlat ve outSnaps'i temizle (conflict önlemi)
  if (!panel.userData.outs) {
    panel.userData.outs = [];
  }
  
  // outSnaps array'ini temizle (eski sistem ile conflict önlemi)
  if (panel.userData.outSnaps) {
     panel.userData.outSnaps = [];
  }
  
  // Mevcut çıkışı bul (lineId, socketId veya id ile)
  const existingExitIndex = panel.userData.outs.findIndex(exit => {
    const dataId = exitData.lineId || exitData.socketId;
    return exit.id === dataId || 
           exit.lineId === exitData.lineId || 
           exit.socketId === exitData.socketId||
           (exit.id && dataId && exit.id === dataId);
  });
  
  if (existingExitIndex !== -1) {
    // Mevcut çıkışı güncelle - eksik bilgileri ekle
    
    const existingExit = panel.userData.outs[existingExitIndex];
    panel.userData.outs[existingExitIndex] = {
      ...existingExit,
      type: exitData.type || existingExit.type,
      lineId: exitData.lineId || existingExit.lineId,
      socketId: exitData.socketId || existingExit.socketId,
      vertices: exitData.vertices || existingExit.vertices,
      ...exitData // Diğer tüm bilgileri ekle
    };
    

    return;
  }
  
  // Mevcut çıkış bulunamadı - bu normal değil, çıkış zaten başka yerde eklenmeli
}

// Ensure that when panel.userData.outs is changed programmatically we also refresh titles
export function notifyPanelOutsChanged(panel) {
  const findScene = (obj) => {
    let cur = obj;
    while (cur) {
      if (cur.type === 'Scene') return cur;
      cur = cur.parent;
    }
    return null;
  };
  const scene = findScene(panel);
  if (scene && panel.userData && panel.userData.id) {
    try {
      updatePanelTitles(scene, panel.userData.id);
    } catch (e) {
      // ignore
    }
  }
}

/**
 * Panel çıkış sayısını hesaplar
 * Önce mevcut exit point'leri sayar, yoksa outs dizisinden alır.
 * Bu sayede wire başlatılıp henüz tamamlanmamışken de doğru sayı döner.
 *
 * @param {THREE.Object3D} panel - Panel object
 * @returns {number} - Çıkış sayısı
 */
export function calculatePanelExitCount(panel) {
  if (!panel || !panel.userData) return 0;

  const userData = panel.userData;

  // ✅ Önce mevcut exit point'leri say (wire başlatılmış ama tamamlanmamış olabilir)
  const existingExitPoints = findPanelExitPoints(panel);
  if (existingExitPoints.length > 0) {
    return existingExitPoints.length;
  }

  // ✅ Exit point yoksa outs dizisini kullan
  if (userData.outs && Array.isArray(userData.outs)) {
    return userData.outs.length;
  }

  // Hiçbiri bulunamazsa 0 döndür (yeni panel)
   return 0;
}

/**
 * Panel exit durumunu kaydeder (snapshot)
 * @param {THREE.Scene} scene - Three.js scene
 * @param {THREE.Object3D} panel - Panel object
 * @returns {Object} Panel exit state snapshot
 */
export function savePanelExitState(scene, panel) {
  if (!panel || !panel.userData) return null;

  const state = {
    outs: panel.userData.outs ? JSON.parse(JSON.stringify(panel.userData.outs)) : [],
    wirePositions: [],
    exitPointPositions: [],
    bellTrafoPositions: [], // ✅ BellTrafo pozisyonlarını kaydetmek için
    // ✅ Panel boyut bilgilerini kaydet
    panelHeight: panel.userData.height,
    panelStartH: panel.userData.startH,
    panelEndH: panel.userData.endH,
    exitCount: calculatePanelExitCount(panel)
  };

  // Mevcut wire pozisyonlarını kaydet
  const panelID = panel.userData.id;
  const wiringLines = findPanelWiringLines(scene, panelID);

  wiringLines.forEach(line => {
    const positions = line.geometry?.attributes?.position;
    if (positions && positions.count >= 2) {
      state.wirePositions.push({
        wireId: line.userData.id,
        firstPoint: new THREE.Vector3(positions.getX(0), positions.getY(0), positions.getZ(0)),
        secondPoint: new THREE.Vector3(positions.getX(1), positions.getY(1), positions.getZ(1))
      });
    }
  });

  // Mevcut exit point pozisyonlarını kaydet
  const exitPoints = findPanelExitPoints(panel);
  exitPoints.forEach((exitPoint, index) => {
    const worldPos = new THREE.Vector3();
    exitPoint.getWorldPosition(worldPos);
    state.exitPointPositions.push({
      index,
      position: worldPos.clone()
    });
  });

  // ✅ BellTrafo pozisyonlarını kaydet
  const outs = panel.userData.outs || [];
  outs.forEach((out, index) => {
    if (out && out.branchType === 'bell' && out.branchID) {
      const bellTrafo = findBellTrafoForBranch(scene, out.branchID);
      if (bellTrafo) {
        const trafoPos = new THREE.Vector3();
        bellTrafo.getWorldPosition(trafoPos);
        state.bellTrafoPositions.push({
          branchID: out.branchID,
          position: trafoPos.clone(),
          rotation: bellTrafo.rotation.z // BellTrafo'nun rotasyonunu da kaydet
        });
      }
    }
  });

  return state;
}

/**
 * Panel exit durumunu geri yükler (restore)
 * @param {THREE.Scene} scene - Three.js scene
 * @param {THREE.Object3D} panel - Panel object
 * @param {Object} state - Kaydedilmiş panel exit state
 */
export function restorePanelExitState(scene, panel, state) {
  if (!panel || !panel.userData || !state) return;

  // Outs dizisini geri yükle
  panel.userData.outs = state.outs ? JSON.parse(JSON.stringify(state.outs)) : [];

  // ✅ Panel boyutunu geri yükle (exit count'a göre)
  const exitCount = state.exitCount || 0;

  // Panel boyutunu ve geometrisini geri yükle
  updatePanelSize(panel, exitCount, scene);

  // Yeşil noktaları geri yükle
  updatePanelExitPoints(panel, exitCount, scene);

  // Wire pozisyonlarını geri yükle
  const panelID = panel.userData.id;
  const wiringLines = findPanelWiringLines(scene, panelID);

  state.wirePositions.forEach(savedWire => {
    const line = wiringLines.find(l => l.userData.id === savedWire.wireId);
    if (line && line.geometry?.attributes?.position) {
      const positions = line.geometry.attributes.position;
      if (positions.count >= 2) {
        positions.setXYZ(0, savedWire.firstPoint.x, savedWire.firstPoint.y, savedWire.firstPoint.z);
        positions.setXYZ(1, savedWire.secondPoint.x, savedWire.secondPoint.y, savedWire.secondPoint.z);
        positions.needsUpdate = true;
        line.geometry.computeBoundingBox();
        line.geometry.computeBoundingSphere();
      }
    }
  });

  // ✅ BellTrafo pozisyonlarını geri yükle
  if (state.bellTrafoPositions && Array.isArray(state.bellTrafoPositions)) {
    state.bellTrafoPositions.forEach(savedTrafo => {
      const bellTrafo = findBellTrafoForBranch(scene, savedTrafo.branchID);
      if (bellTrafo && savedTrafo.position) {
        // BellTrafo'yu kaydedilmiş pozisyona taşı
        updateBellTrafoPosition(bellTrafo, savedTrafo.position);
        // Rotation'ı da geri yükle
        if (savedTrafo.rotation !== undefined) {
          bellTrafo.rotation.z = savedTrafo.rotation;
        }
      }
    });
  }
}

/**
 * Panel'den son eklenen çıkışı kaldırır (ESC iptal için)
 * @param {THREE.Scene} scene - Three.js scene
 * @param {THREE.Object3D} panel - Panel object
 * @param {string} addPosition - 'start' veya 'end' - son çıkışın hangi pozisyonda eklendiği
 */
export function removeLastPanelExit(scene, panel, addPosition = 'start') {
  if (!panel || !panel.userData) return;

  const currentExitCount = calculatePanelExitCount(panel);

  // Çıkış yoksa hiçbir şey yapma
  if (currentExitCount === 0) return;

  // Son eklenen çıkışı kaldır
  if (addPosition === 'end') {
    // 'end' pozisyonunda eklenmişse, ilk elemanı kaldır (array başından)
    if (panel.userData.outs && panel.userData.outs.length > 0) {
      panel.userData.outs.shift();
    }
  } else {
    // 'start' pozisyonunda eklenmişse, son elemanı kaldır (array sonundan)
    if (panel.userData.outs && panel.userData.outs.length > 0) {
      panel.userData.outs.pop();
    }
  }

  // Yeni çıkış sayısını hesapla
  const newExitCount = calculatePanelExitCount(panel);

  // Yeşil noktaları güncelle
  updatePanelExitPoints(panel, newExitCount, scene);

  // Kalan çıkışlar varsa, onları yeni yeşil noktalara yerleştir
  if (newExitCount > 0) {
    updatePanelWiringPositions(scene, panel, addPosition);
  }
}

/**
 * Panel'e bağlı tüm çıkış hatlarını bulur
 * @param {THREE.Scene} scene - Three.js scene
 * @param {string} panelID - Panel ID
 * @returns {THREE.Line[]} - Panel'e bağlı çıkış hatları
 */
export function findPanelWiringLines(scene, panelID) {
  const wiringLines = [];
  // uuid set ile aynı line'ın iki kez listelenmesini engelle — traverse içine giren
  // group child'ları üst seviyeden de yakalanabiliyor, fallback unhandledLines mantığı
  // bu duplicate'leri ayrı wire sanıp iki ayrı exit'e atayarak duplicate görselli wire
  // izlenimi yaratabiliyor.
  const seen = new Set();

  scene.traverse((child) => {
    if (child.type === 'Line' && child.geometry && child.userData) {
      const positions = child.geometry.attributes.position;
      if (positions && positions.count > 0) {
        const ud = child.userData;
        // Panel'e bağlı wire field'larından herhangi biri eşleşirse dahil et.
        // Panel besleme wire'ı (useElectricalPanelWiring) `sourcePanelId` set ediyor,
        // socket/lighting wire'ı `panelID`, ana panel besleme wire'ı `sourceId`.
        // Sadece panelID kontrol etmek panel besleme wire'larını dışarıda bırakıyordu
        // → ESC ile iptal sonrası wire'ların ilk vertex'leri yeşil noktalara hizalanmıyordu.
        if (
          ud.panelID === panelID ||
          ud.sourcePanelId === panelID ||
          ud.sourceId === panelID ||
          ud.sourcePanelID === panelID
        ) {
          if (!seen.has(child.uuid)) {
            seen.add(child.uuid);
            wiringLines.push(child);
          }
        }
      }
    }
  });

  return wiringLines;
}

/**
 * Panel çıkış hatlarının ilk noktasını yeşil noktalara göre günceller
 * @param {THREE.Scene} scene - Three.js scene  
 * @param {THREE.Object3D} panel - Panel object
 * @param {string} addPosition - 'start' veya 'end' (opsiyonel)
 */
export function updatePanelWiringPositions(scene, panel, addPosition = null) {
  const panelID = panel.userData.id;
  const panelType = panel.userData.type;
  const exitPoints = findPanelExitPoints(panel);
  const allWiringLines = findPanelWiringLines(scene, panelID);
  const outs = panel.userData.outs || [];

  if (allWiringLines.length === 0) {
    return;
  }
  const sortedWiringLines = []; // { wire, out } objeleri

  // Her out için karşılık gelen wire'ı bul
  outs.forEach((out, outsIndex) => {
    let matchingWire = null;
    // out.wires array'inde wire ID'si var mı kontrol et
    if (out.wires && out.wires.length > 0) {
      const wireData = out.wires[0]; // İlk wire data'sını al
      if (!wireData) {
        return;
      }
      const wireId = wireData.id || wireData; // Object veya string olabilir
      matchingWire = allWiringLines.find(wire => wire.userData.id === wireId);
    }

    // ✅ REDO uyumluluğu: out.wireId ile de eşleştirmeyi dene
    if (!matchingWire && out.wireId) {
      matchingWire = allWiringLines.find(wire => wire.userData.id === out.wireId);
    }

    // ✅ feederId ile de eşleştirmeyi dene — undo+redo sonrası wire.userData.id
    // bazen feederData.wireID değil feederData.id'ye düşebiliyor (cmd.execute path).
    if (!matchingWire && out.feederId) {
      matchingWire = allWiringLines.find(wire =>
        wire.userData.feederId === out.feederId ||
        wire.userData.id === out.feederId
      );
    }

    // ✅ REDO uyumluluğu: out.loadId ile de eşleştirmeyi dene (lightingLoadId, machineLoadId, etc.)
    if (!matchingWire && out.loadId) {
      matchingWire = allWiringLines.find(wire =>
        wire.userData.lightingLoadId === out.loadId ||
        wire.userData.machineLoadId === out.loadId ||
        wire.userData.socketLoadId === out.loadId ||
        wire.userData.loadId === out.loadId
      );
    }

    // Bulunamazsa branchID ile userData eşleştirmeyi dene
    if (!matchingWire && out.branchID) {
      matchingWire = allWiringLines.find(wire => wire.userData.branchID === out.branchID);
    }

    // ✅ Bell wire için: function === 'wireBell' kontrolü
    if (!matchingWire && out.branchType === 'bell') {
      matchingWire = allWiringLines.find(wire =>
        wire.userData.function === 'wireBell' &&
        (wire.userData.branchID === out.branchID || !wire.userData.branchID)
      );
    }

    if (matchingWire) {
      // ✅ outs INDEX'inde sakla (sparse) — eşleşmeyen outs gap bırakır.
      // Compact push yapılırsa: outs[i] eşleşmezse outs[i+1] sortedWiringLines[i]'e
      // düşer, exit point ataması kayar (yanlış wire yanlış exit'e gider).
      sortedWiringLines[outsIndex] = { wire: matchingWire, out: out };
    } else {
      console.warn(`⚠️ No wire found for outs[${outsIndex}]`, out);
    }
  });
  
  // ─── FALLBACK ───
  // outs'ta olmayan ama panele bağlı (userData.panelID === panelID) wire'ları
  // da kaydırmaya dahil et. Wire henüz tamamlanmadan veya socket/lighting
  // yerleştirilmeden önce exit eklenirse outs boş kalır — bu durumda wire'ı
  // mevcut ilk vertex pozisyonuna göre sırayla eşleştirip kaydırırız.
  //
  // ÖNEMLİ: panel-outer, frontEdge gibi PANEL'İN KENDİ yapısal çizgileri de
  // userData.panelID taşır. Bunları wire sayıp kaydırırsak panelin çerçevesi
  // bozulur. `isPanelPart` ve wire-like type kontrolüyle dışla.
  const isWireLike = (w) => {
    const ud = w.userData || {};
    if (ud.isPanelPart) return false;             // panel parçaları (frame, hatch, frontEdge)
    if (ud.type === 'wireTitle') return false;     // title group lines
    if (ud.lineType === 'frontEdge') return false; // front edge
    // ✅ Branch wire'lar (mevcut bir wire'dan dallanan socket/aydınlatma hattı) panele
    // exit point üzerinden bağlanmaz, parent wire'ın junction noktasından çıkar.
    // İlk vertex'i panel exit point'e taşımak parent bağlantısını koparır → exclude.
    // useSocket/useLighting bu wire'lara userData.sourceType = 'socketBuat'|'socketVertex'|
    // 'lightingBuat'|'lightingVertex' set ediyor. (Daha önce sadece socket dışlanıyordu →
    // branch aydınlatma hattının ilk vertex'i panele sürükleniyordu.)
    if (ud.sourceType === 'socketBuat' || ud.sourceType === 'socketVertex' ||
        ud.sourceType === 'lightingBuat' || ud.sourceType === 'lightingVertex') return false;
    // feederWire / wireLighting / wireSocket / wireMachine / wireBell — wire gibi türler
    const t = ud.type || '';
    return t === 'feederWire' || t.startsWith('wire') || ud.feederId || ud.wireId || ud.wireID;
  };
  const alreadyHandled = new Set(
    sortedWiringLines.filter(it => it).map(it => it.wire.uuid)
  );
  const unhandledLines = allWiringLines.filter(w => !alreadyHandled.has(w.uuid) && isWireLike(w));
  if (unhandledLines.length > 0) {
    // Panel koordinat sisteminde perpendicular offset'e göre sırala — böylece
    // mevcut sıralarını koruyarak yeni exit point'lere atanırlar.
    const panelAngleRad = (panel.rotation?.z || 0);
    const nx = -Math.sin(panelAngleRad), ny = Math.cos(panelAngleRad);
    unhandledLines.sort((a, b) => {
      const aPos = a.geometry.attributes.position;
      const bPos = b.geometry.attributes.position;
      const aOff = aPos.getX(0) * nx + aPos.getY(0) * ny;
      const bOff = bPos.getX(0) * nx + bPos.getY(0) * ny;
      return aOff - bOff;
    });
    // Sparse sortedWiringLines'taki ilk gap'lere yerleştir
    let unhandledIdx = 0;
    for (let i = 0; i < outs.length && unhandledIdx < unhandledLines.length; i++) {
      if (!sortedWiringLines[i]) {
        sortedWiringLines[i] = { wire: unhandledLines[unhandledIdx++], out: null };
      }
    }
    // Hala kalan unhandled varsa sona ekle
    while (unhandledIdx < unhandledLines.length) {
      sortedWiringLines.push({ wire: unhandledLines[unhandledIdx++], out: null });
    }
  }

  // Sparse array — non-null entry yoksa erken çık
  if (!sortedWiringLines.some(it => it)) {
    return;
  }

  // addPosition'a göre offset belirleme
  let startOffset = 0;
  if (addPosition === 'end') {
    startOffset = 1; // İlk yeşil nokta boş kalacak, index 1'den başla
  } else {
    startOffset = 0; // Normal yerleştirme, index 0'dan başla
  }

  // ✅ Kullanıcının manuel sıralamasını koru: wire'ları outs INDEX'i yerine wire'ların
  // MEVCUT ilk-vertex pozisyonuna (panel kenarı boyunca perpendicular offset) göre sırala.
  // Kullanıcı çıkışları sürükleyip yeniden sıraladıktan sonra yeni bir çıkış eklenince
  // outs sırası geri yüklenip manuel sıra bozuluyordu. Mevcut pozisyona göre sıralamak
  // hem manuel sırayı korur hem yeni çıkışı kendi konumuna oturtur. Her wire kendi
  // out metadata'sını taşıdığı için eşleştirme bozulmaz.
  const _pAngle = (panel.rotation?.z || 0);
  const _pnx = -Math.sin(_pAngle), _pny = Math.cos(_pAngle);
  const orderedPairs = sortedWiringLines.filter(it => it);
  orderedPairs.sort((a, b) => {
    const ap = a.wire.geometry?.attributes?.position;
    const bp = b.wire.geometry?.attributes?.position;
    if (!ap || !bp) return 0;
    const aOff = ap.getX(0) * _pnx + ap.getY(0) * _pny;
    const bOff = bp.getX(0) * _pnx + bp.getY(0) * _pny;
    return aOff - bOff;
  });

  // Offset ile wire'ları yerleştir
  orderedPairs.forEach((item, index) => {
    if (!item) return;
    const { wire: line, out } = item;
    const exitPointIndex = startOffset + index;

    if (exitPointIndex >= exitPoints.length) {
      console.warn(`Wire ${index} has no corresponding exit point (trying index ${exitPointIndex})`);
      return;
    }

    const exitPoint = exitPoints[exitPointIndex];
    const worldPosition = new THREE.Vector3();
    exitPoint.getWorldPosition(worldPosition);

    // ✅ Eğer bu wire'ın bellTrafo'su varsa, trafoyu da taşı
    if (out && out.branchType === 'bell') {
      // Bell trafo'yu bul ve taşı
      const bellTrafo = findBellTrafoForBranch(scene, out.branchID);
      if (bellTrafo) {
          updateBellTrafoPosition(bellTrafo, worldPosition);

        // Trafo taşındıktan sonra yeni output pozisyonunu al
        const trafoOutputPoint = new THREE.Vector3();
        if (bellTrafo.userData.outputMarker) {
          bellTrafo.userData.outputMarker.getWorldPosition(trafoOutputPoint);
          // Wire'ı trafo output'undan başlat
          updateWireFirstTwoPointsWithVector(line, trafoOutputPoint);
        } else {
          updateWireFirstTwoPointsWithVector(line, worldPosition);
        }
      } else {
        updateWireFirstTwoPointsWithVector(line, worldPosition);
      }
    } else {
      updateWireFirstTwoPointsWithVector(line, worldPosition);
    }
   });

 }

/**
 * Bell trafoyu branchID'ye göre bulur
 * @param {THREE.Scene} scene - Three.js scene
 * @param {string} branchID - Branch ID
 * @returns {THREE.Group|null} Bell trafo object veya null
 */
export function findBellTrafoForBranch(scene, branchID) {
  let trafo = null;
  // First try to find a true trafo object by branchID, preferring objects
  // that look like trafos (type contains 'trafo' or have outputMarker/outputPoint)
  if (branchID) {
    scene.traverse((obj) => {
      const ud = obj.userData || {};
      if (ud.branchID === branchID || ud.id === branchID) {
        const t = (ud.type || '').toString().toLowerCase();
        // Only consider it a trafo if type mentions 'trafo' or it has explicit output marker/point
        const looksLikeTrafo = t.includes('trafo') || !!ud.outputMarker || !!ud.outputPoint;
        // Exclude bell symbol (type === 'bell')
        const isBellSymbol = t === 'bell';
        if (looksLikeTrafo && !isBellSymbol) {
          trafo = obj;
        }
      }
    });
    if (trafo) return trafo;
  }

  // If above didn't find by branchID as a trafo, try to find any object that
  // explicitly has type 'belltrafo' (case-insensitive) or an outputMarker
  scene.traverse((obj) => {
    const ud = obj.userData || {};
    const t = ud.type && ud.type.toString().toLowerCase();
    const looksLikeTrafo = (t === 'belltrafo') || !!ud.outputMarker || !!ud.outputPoint;
    const isBellSymbol = t === 'bell';
    if (looksLikeTrafo && !isBellSymbol) {
      trafo = obj;
    }
  });
  return trafo;
}

/**
 * Bell trafo pozisyonunu günceller
 * @param {THREE.Group} bellTrafo - Bell trafo object
 * @param {THREE.Vector3} newPosition - Yeni pozisyon
 */
export function updateBellTrafoPosition(bellTrafo, newPosition) {
  if (!bellTrafo || !newPosition) return;
  const worldPos = newPosition.clone();
  try {
    if (bellTrafo.parent && typeof bellTrafo.parent.worldToLocal === 'function') {
      const local = bellTrafo.parent.worldToLocal(worldPos);
      bellTrafo.position.copy(local);
    } else {
      bellTrafo.position.set(worldPos.x, worldPos.y, worldPos.z || 0);
    }
  } catch (e) {
    // Fallback to direct set
    try { bellTrafo.position.set(worldPos.x, worldPos.y, worldPos.z || 0); } catch (e2) {}
  }
  bellTrafo.updateMatrixWorld(true);
}

/**
 * Çıkış hattının ilk iki noktasını vektörel hareket ile günceller
 * @param {THREE.Line} line - Çıkış hattı
 * @param {THREE.Vector3} newFirstPoint - Yeni ilk nokta (yeşil daire pozisyonu)
 */
function updateWireFirstTwoPointsWithVector(line, newFirstPoint) {
  const geometry = line.geometry;
  const positions = geometry.attributes.position;
  
  if (!positions || positions.count < 2) {
    console.warn('Wire line has insufficient points for vector update');
    return;
  }
  
  // Mevcut ilk noktayı al
  const oldFirstPoint = new THREE.Vector3(
    positions.getX(0),
    positions.getY(0), 
    positions.getZ(0)
  );
  
  // Mevcut ikinci noktayı al
  const oldSecondPoint = new THREE.Vector3(
    positions.getX(1),
    positions.getY(1),
    positions.getZ(1)
  );
  
  // Hareket vektörünü hesapla (yeni pozisyon - eski pozisyon)
  const movementVector = newFirstPoint.clone().sub(oldFirstPoint);
  
  // İkinci noktanın yeni pozisyonunu hesapla (eski ikinci nokta + hareket vektörü)
  const newSecondPoint = oldSecondPoint.clone().add(movementVector);
  
  // Her iki noktayı da güncelle
  positions.setXYZ(0, newFirstPoint.x, newFirstPoint.y, newFirstPoint.z);
  positions.setXYZ(1, newSecondPoint.x, newSecondPoint.y, newSecondPoint.z);
  
  // Geometry'yi güncelle
  positions.needsUpdate = true;
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  
 }

/**
 * Çıkış hattının sadece ilk noktasını günceller (eski versiyon - gerekirse kullanılır)
 * @param {THREE.Line} line - Çıkış hattı
 * @param {THREE.Vector3} newFirstPoint - Yeni ilk nokta (yeşil daire pozisyonu)
 */
function updateWireFirstPoint(line, newFirstPoint) {
  const geometry = line.geometry;
  const positions = geometry.attributes.position;
  
  if (!positions || positions.count < 1) {
    console.warn('Wire line has no points');
    return;
  }
  
  // Sadece ilk noktayı güncelle
  positions.setXYZ(0, newFirstPoint.x, newFirstPoint.y, newFirstPoint.z);
  
  // Geometry'yi güncelle
  positions.needsUpdate = true;
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
}

/**
 * Çıkış hattının ilk 2 noktasını günceller (gelecekte kullanılacak)
 * @param {THREE.Line} line - Çıkış hattı
 * @param {THREE.Vector3} newFirstPoint - Yeni ilk nokta (yeşil daire pozisyonu)
 * @param {THREE.Object3D} panel - Panel object (direction için)
 */
function updateWireFirstTwoPoints(line, newFirstPoint, panel) {
  const geometry = line.geometry;
  const positions = geometry.attributes.position;
  
  if (!positions || positions.count < 2) {
    console.warn('Wire line has insufficient points');
    return;
  }
  
  // Eski ilk ve ikinci noktaları al
  const oldFirstPoint = new THREE.Vector3(
    positions.getX(0),
    positions.getY(0), 
    positions.getZ(0)
  );
  
  const oldSecondPoint = new THREE.Vector3(
    positions.getX(1),
    positions.getY(1),
    positions.getZ(1)
  );
  
  // Direction vektörünü hesapla (ikinci nokta - ilk nokta)
  const direction = oldSecondPoint.clone().sub(oldFirstPoint);
  const distance = direction.length();
  direction.normalize();
  
  // Panel'in ön yönünü al (dik çıkış için)
  const panelRotation = panel.rotation.z;
  const panelFrontDir = new THREE.Vector3(
    Math.cos(panelRotation), 
    Math.sin(panelRotation), 
    0
  ).normalize();
  
  // Yeni ikinci nokta = yeni ilk nokta + (panel direction * distance)
  const newSecondPoint = newFirstPoint.clone().add(panelFrontDir.multiplyScalar(distance));

  // Pozisyonları güncelle
  positions.setXYZ(0, newFirstPoint.x, newFirstPoint.y, newFirstPoint.z);
  positions.setXYZ(1, newSecondPoint.x, newSecondPoint.y, newSecondPoint.z);

  // Geometry'yi güncelle
  positions.needsUpdate = true;
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();

 }

/**
 * Panel çıkışları yer değiştirdiğinde etiket numaralarını günceller
 * Etiketler, wire'ların bağlı olduğu panel çıkış noktasına göre numaralandırılır
 * @param {THREE.Scene} scene - Three.js scene
 * @param {string} panelID - Panel ID
 * @returns {Array<{componentId: string, exitOrder: number}>} Redux güncellemesi için exitOrder mapping
 */
export function updateWireTitleNumbers(scene, panelID, feeders = []) {
 
  if (!scene || !panelID) return [];

  // Panel'i bul
  let panel = null;
  scene.traverse((obj) => {
    if (obj.userData?.id === panelID) {
      panel = obj;
    }
  });

  if (!panel) {
     return [];
  }

  // Panel çıkış noktalarını bul (yeşil yuvarlaklar)
  let exitPoints = findPanelExitPoints(panel);

  // Çıkış noktalarını world Y koordinatına göre sırala (en alttan en üste)
  exitPoints = exitPoints
    .map(point => {
      const worldPos = new THREE.Vector3();
      point.getWorldPosition(worldPos);
      return { point, y: worldPos.y };
    })
    .sort((a, b) => a.y - b.y)
    .map(obj => obj.point);

   if (exitPoints.length === 0) return [];

  // Panel'e bağlı tüm wire'ları bul
  const panelWires = [];
  scene.traverse((obj) => {
    if (obj.type === 'Line' && obj.userData?.panelID === panelID) {
      panelWires.push(obj);
    }
  });
 
  // Mevcut outs dizisini al
  const currentOuts = panel.userData.outs || [];
 
  // Yeni sıralanmış outs dizisi
  const newOrderedOuts = [];

  // Her çıkış noktası için sırayla işle
  exitPoints.forEach((exitPoint, index) => {
    const exitNumber = index + 1;

    // Çıkış noktasının world pozisyonunu al
    const exitWorldPos = new THREE.Vector3();
    exitPoint.getWorldPosition(exitWorldPos);

    // Bu çıkış noktasına en yakın wire'ı bul
    let closestWire = null;
    let minDist = Infinity;

    // Önce bu çıkış noktasında bellTrafo var mı kontrol et
    let bellTrafoAtExit = null;
    scene.traverse((obj) => {
      if (obj.userData?.type === 'bellTrafo' ||
          (obj.userData?.type && obj.userData.type.toString().toLowerCase().includes('trafo'))) {
        const trafoPos = new THREE.Vector3();
        obj.getWorldPosition(trafoPos);
        const dist = trafoPos.distanceTo(exitWorldPos);
        if (dist < 5) {
          bellTrafoAtExit = obj;
        }
      }
    });

    panelWires.forEach((wire) => {
      const positions = wire.geometry?.attributes?.position;
      if (!positions || positions.count < 1) return;

      const firstPoint = new THREE.Vector3(
        positions.getX(0),
        positions.getY(0),
        positions.getZ(0)
      );

      // Eğer bu çıkışta bellTrafo varsa ve wire bell tipindeyse
      if (bellTrafoAtExit && wire.userData?.function === 'wireBell') {
        // BellTrafo'nun branchID'si ile wire'ın branchID'sini eşleştir
        if (bellTrafoAtExit.userData?.branchID === wire.userData?.branchID) {
          closestWire = wire;
          minDist = 0;
          return;
        }
      }

      const dist = firstPoint.distanceTo(exitWorldPos);
      if (dist < minDist) {
        minDist = dist;
        closestWire = wire;
      }
    });

    if (!closestWire || minDist > 15) {
       return;
    }

    const wireId = closestWire.userData?.id;
 
    // Wire'ın userData'sına exitIndex kaydet
    closestWire.userData.exitIndex = index;

    // Bu wire'a karşılık gelen out verisini bul
    let matchingOut = currentOuts.find(out => {
      // wireId ile eşleştir
      if (out.wires && out.wires.length > 0) {
        const outWireId = out.wires[0]?.id || out.wires[0];
        if (outWireId === wireId) return true;
      }
      if (out.lineId === wireId) return true;
      if (out.wireId === wireId) return true;

      // branchID veya loadId ile eşleştir
      if (out.branchID && closestWire.userData?.branchID === out.branchID) return true;
      if (out.loadId && (
        closestWire.userData?.lightingLoadId === out.loadId ||
        closestWire.userData?.machineLoadId === out.loadId ||
        closestWire.userData?.socketLoadId === out.loadId
      )) return true;

      return false;
    });

    if (matchingOut) {
      // ✅ Wire'dan component ID'yi al (Redux güncellemesi için)
      const wireComponentId = closestWire.userData?.lightingLoadId ||
                              closestWire.userData?.machineLoadId ||
                              closestWire.userData?.socketLoadId ||
                              closestWire.userData?.loadId ||
                              closestWire.userData?.componentId;

      // Out'u yeni sıraya ekle - wire'dan alınan componentId'yi de ekle
      newOrderedOuts.push({
        ...matchingOut,
        exitIndex: index,
        componentId: wireComponentId || matchingOut.loadId || matchingOut.socketId || matchingOut.componentId || matchingOut.id
      });
     }
  });

  // Panel userData.outs'u güncelle
  if (newOrderedOuts.length > 0) {
    panel.userData.outs = newOrderedOuts;
   }

  // ✅ Wire'ları exitIndex'e göre bir map'e koy
  const wiresByExitIndex = {};
  panelWires.forEach(wire => {
    if (wire.userData.exitIndex !== undefined) {
      wiresByExitIndex[wire.userData.exitIndex] = wire;
    }
  });

  // ✅ Title'ları exitIndex'e göre bir map'e koy
  const titlesByExitIndex = {};
  scene.traverse((obj) => {
    if (obj.userData?.type === 'wireTitle' &&
        (obj.userData?.panelId === panelID || obj.userData?.panelID === panelID)) {
      const titleExitIndex = obj.userData?.exitIndex;
      if (titleExitIndex !== undefined) {
        titlesByExitIndex[titleExitIndex] = obj;
      }
    }
  });

  // ✅ Her exit pozisyonu için title'ı doğru wire'a bağla
  Object.keys(wiresByExitIndex).forEach(exitIndexStr => {
    const exitIndex = parseInt(exitIndexStr);
    const wire = wiresByExitIndex[exitIndex];
    const title = titlesByExitIndex[exitIndex];

    if (!wire || !title) return;

    // Wire'ın güncel vertex'lerini al
    const positions = wire.geometry?.attributes?.position;
    if (!positions || positions.count < 2) return;

    const newVertices = [];
    for (let i = 0; i < positions.count; i++) {
      newVertices.push(new THREE.Vector3(
        positions.getX(i),
        positions.getY(i),
        positions.getZ(i)
      ));
    }

    // ✅ Title'ın wireVertices'ini güncelle
    title.userData.wireVertices = newVertices;

    // ✅ Title'ın pozisyonunu wire üzerindeki mesafeye göre güncelle
    const distanceAlongWire = title.userData.distanceAlongWire || 30;
    const newPosition = getPointAlongPolyline(newVertices, distanceAlongWire);
    if (newPosition) {
      title.position.copy(newPosition);
    }

    // ✅ Title'ın rotasyonunu wire yönüne göre güncelle
    if (newVertices.length >= 2) {
      const direction = new THREE.Vector3().subVectors(newVertices[1], newVertices[0]).normalize();
      const baseAngle = Math.atan2(direction.y, direction.x);
      title.rotation.z = baseAngle;
    }

    // ✅ Title numarasını güncelle
    const newNumber = exitIndex + 1;
    title.traverse((child) => {
      if (child.name === 'titleLabel' && child.userData?.drawText) {
        child.userData.drawText(String(newNumber));
        child.userData.text = String(newNumber);

        if (child.userData.texture) {
          child.userData.texture.needsUpdate = true;
        }
      }
    });

   });

  // ✅ Redux güncellemesi için feeder titleNumber mapping oluştur
  // Her feeder için yeni titleNumber değerini hesapla
  const feederTitleUpdates = [];

  Object.keys(wiresByExitIndex).forEach(exitIndexStr => {
    const exitIndex = parseInt(exitIndexStr);
    const wire = wiresByExitIndex[exitIndex];

    if (!wire) return;

    // ✅ Wire'dan feederId al
    let feederId = wire.userData?.feederId;

    // ✅ Eğer wire.userData.feederId yoksa, feeders içinde metadata.wireId eşleştirmesi yap
    if (!feederId && feeders && feeders.length > 0) {
      const wireId = wire.userData?.id;
      if (wireId) {
        const matchedFeeder = feeders.find(f => f.metadata?.wireId === wireId);
        if (matchedFeeder) {
          feederId = matchedFeeder.id;
         }
      }
    }

    // ✅ Hala bulunamadıysa, wire.userData.id'yi kullan (eski davranış - uyumsuzluk olabilir)
    if (!feederId) {
      feederId = wire.userData?.id;
      console.warn(`⚠️ Feeder eşleştirmesi bulunamadı, wire.id kullanılıyor: ${feederId}`);
    }

    const titleNumber = exitIndex + 1; // 1-based

    if (feederId) {
      feederTitleUpdates.push({
        feederId,
        titleNumber
      });
     } else {
      console.warn(`⚠️ Çıkış ${titleNumber}: feederId bulunamadı`, wire.userData);
    }
  });

 
  return feederTitleUpdates;
}